import { useEffect, useState } from "react";
import {
  Trash2,
  Search,
  ChevronUp,
  ChevronDown,
  Building2,
  GraduationCap,
  ShieldCheck,
  Users,
} from "lucide-react";
import AdminLayout from "../../layouts/AdminLayout";
import { useToast } from "../../components/ToastContext";
import { getAdminUsers } from "../../api/services/platformAdminService";

const ROLE_CONFIG = {
  company_admin: {
    label: "Company Admin",
    icon: Building2,
    className: "bg-sky-500/10 text-sky-400 border-sky-500/20",
  },
  university_admin: {
    label: "University Admin",
    icon: GraduationCap,
    className: "bg-violet-500/10 text-violet-400 border-violet-500/20",
  },
  platform_admin: {
    label: "Platform Admin",
    icon: ShieldCheck,
    className: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  },
};

const ROLE_FILTERS = [
  { value: "all", label: "All Roles" },
  { value: "company_admin", label: "Company Admin" },
  { value: "university_admin", label: "University Admin" },
  { value: "platform_admin", label: "Platform Admin" },
];

export default function UserManagement() {
  const { showToast } = useToast();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortField, setSortField] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    const loadUsers = async () => {
      setIsLoading(true);
      try {
        const data = await getAdminUsers();
        const items = Array.isArray(data) ? data : [];
        const mapped = items.map((user) => ({
          id: user.user_id,
          username: user.username || user.email || "—",
          email: user.email || "—",
          role: user.role,
          organization: user.organization_name || "—",
          createdAt: user.joined_at || "",
        }));
        setUsers(mapped);
      } catch (error) {
        showToast(error.message || "Failed to load users", "error");
      } finally {
        setIsLoading(false);
      }
    };

    loadUsers();
  }, [showToast]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, roleFilter]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  // const handleDelete = (id) => {
  //   setUsers((prev) => prev.filter((u) => u.id !== id));
  //   setDeleteTarget(null);
  // };

  const filtered = users
    .filter((u) => {
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        u.username.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.organization.toLowerCase().includes(q);
      const matchRole = roleFilter === "all" || u.role === roleFilter;
      return matchSearch && matchRole;
    })
    .sort((a, b) => {
      const aVal = a[sortField] ?? "";
      const bVal = b[sortField] ?? "";
      return sortDir === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const pagedUsers = filtered.slice(startIndex, endIndex);

  const pageNumbers = (() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (safePage <= 3) return [1, 2, 3, 4, "…", totalPages];
    if (safePage >= totalPages - 2) {
      return [
        1,
        "…",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }
    return [1, "…", safePage - 1, safePage, safePage + 1, "…", totalPages];
  })();

  const SortIcon = ({ field }) => (
    <span className="inline-flex flex-col ml-1 opacity-40 group-hover:opacity-100 transition-opacity">
      <ChevronUp
        size={10}
        className={
          sortField === field && sortDir === "asc"
            ? "opacity-100 text-(--color-text-primary)"
            : ""
        }
      />
      <ChevronDown
        size={10}
        className={
          sortField === field && sortDir === "desc"
            ? "opacity-100 text-(--color-text-primary)"
            : ""
        }
      />
    </span>
  );

  const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <AdminLayout>
      <div className="px-8 py-8 space-y-6 min-h-screen">
        {/* Page header */}
        <div>
          <h1 className="text-2xl font-bold text-(--color-text-primary)">
            User Management
          </h1>
          <p className="text-xs mt-0.5 text-(--color-text-muted)">
            View and manage all registered administrators on the platform.
          </p>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            {
              label: "Total Users",
              value: users.length,
              icon: Users,
              color: "text-slate-400",
              bg: "bg-slate-500/10",
            },
            {
              label: "Company Admins",
              value: users.filter((u) => u.role === "company_admin").length,
              icon: Building2,
              color: "text-sky-400",
              bg: "bg-sky-500/10",
            },
            {
              label: "University Admins",
              value: users.filter((u) => u.role === "university_admin").length,
              icon: GraduationCap,
              color: "text-violet-400",
              bg: "bg-violet-500/10",
            },
            {
              label: "Platform Admins",
              value: users.filter((u) => u.role === "platform_admin").length,
              icon: ShieldCheck,
              color: "text-amber-400",
              bg: "bg-amber-500/10",
            },
          ].map(({ label, value, icon: Icon, color, bg }) => (
            <div
              key={label}
              className="rounded-lg bg-(--color-bg)/30 dark:bg-(--color-bg-card) border border-(--color-border) px-5 py-4 flex items-center gap-4"
            >
              <div className={`p-2.5 rounded-md ${bg}`}>
                <Icon size={18} className={color} />
              </div>
              <div>
                <p className="text-2xl font-bold text-(--color-text-primary)">
                  {value}
                </p>
                <p className="text-xs text-(--color-text-muted)">{label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Table card */}
        <div className="rounded-md bg-(--color-bg)/30 dark:bg-(--color-bg-card) border border-(--color-border)">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-6 py-4 border-b border-(--color-border)">
            {/* Search */}
            <div className="relative w-full sm:w-72">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-(--color-text-muted)"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-9 pr-4 py-2 rounded-md border border-(--color-border) bg-(--color-bg)/50 text-sm text-(--color-text-primary) placeholder:text-(--color-text-muted) focus:outline-none focus:border-(--color-primary) focus:shadow-[0_0_0_3px_var(--color-primary-100)] transition-all"
              />
            </div>

            {/* Role filter pills */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {ROLE_FILTERS.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setRoleFilter(value)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    roleFilter === value
                      ? "bg-(--color-primary) text-(--color-text-on-primary)"
                      : "bg-(--color-bg)/50 border border-(--color-border) text-(--color-text-muted) hover:text-(--color-text-primary)"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-(--color-border)">
                  {[
                    { label: "Username", field: "username" },
                    { label: "Email", field: "email" },
                    { label: "Role", field: "role" },
                    { label: "Organization", field: "organization" },
                    { label: "Joined", field: "createdAt" },
                  ].map(({ label, field }) => (
                    <th
                      key={field}
                      onClick={() => handleSort(field)}
                      className="group px-6 py-3.5 text-left text-xs font-semibold text-slate-400 uppercase cursor-pointer hover:text-(--color-text-primary) transition-colors select-none whitespace-nowrap"
                    >
                      {label}
                      <SortIcon field={field} />
                    </th>
                  ))}
                  <th className="px-6 py-3.5 text-right text-xs font-semibold text-slate-400 uppercase">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-(--color-border)">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-14 text-center text-sm text-(--color-text-muted)"
                    >
                      Loading users...
                    </td>
                  </tr>
                ) : pagedUsers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-14 text-center text-sm text-(--color-text-muted)"
                    >
                      No users found.
                    </td>
                  </tr>
                ) : (
                  pagedUsers.map((user) => {
                    const role = ROLE_CONFIG[user.role];
                    const RoleIcon = role.icon;
                    return (
                      <tr
                        key={user.id}
                        className="hover:bg-(--color-bg)/50 transition-colors"
                      >
                        <td className="px-6 py-4 font-medium text-(--color-text-primary) whitespace-nowrap">
                          {user.username}
                        </td>
                        <td className="px-6 py-4 text-(--color-text-primary) whitespace-nowrap">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${role.className}`}
                          >
                            <RoleIcon size={11} />
                            {role.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-(--color-text-primary) whitespace-nowrap">
                          {user.organization}
                        </td>
                        <td className="px-6 py-4 text-(--color-text-primary) whitespace-nowrap">
                          {formatDate(user.createdAt)}
                        </td>
                        <td className="px-6 py-4 text-right whitespace-nowrap">
                          <button
                            // onClick={() => setDeleteTarget(user)}
                            disabled
                            className="p-1.5 rounded-md text-(--color-text-muted) hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          >
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Table footer */}
          <div className="px-6 py-3.5 border-t border-(--color-border) flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-xs text-(--color-text-muted)">
              Showing {filtered.length === 0 ? 0 : startIndex + 1}–
              {Math.min(endIndex, filtered.length)} of {filtered.length} users
            </p>
            <div className="flex gap-1.5 flex-wrap">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="px-3 py-1 text-(--color-text-muted) hover:bg-(--color-bg-overlay) rounded transition-colors text-xs disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {pageNumbers.map((p, i) =>
                p === "…" ? (
                  <span
                    key={`ellipsis-${i}`}
                    className="px-2 py-1 text-xs text-(--color-text-muted)"
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setCurrentPage(p)}
                    className={`px-3 py-1 rounded text-xs transition-colors ${
                      p === safePage
                        ? "bg-(--color-primary) text-white dark:text-black"
                        : "text-(--color-text-muted) hover:bg-(--color-bg-overlay)"
                    }`}
                    aria-current={p === safePage ? "page" : undefined}
                  >
                    {p}
                  </button>
                ),
              )}
              <button
                type="button"
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={safePage >= totalPages}
                className="px-3 py-1 text-(--color-text-muted) hover:bg-(--color-bg-overlay) rounded transition-colors text-xs disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-sm mx-4 rounded-2xl bg-(--color-bg-card) border border-(--color-border) shadow-2xl p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-full bg-red-500/10">
                <Trash2 size={18} className="text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-(--color-text-primary)">
                  Delete User
                </h3>
                <p className="text-xs text-(--color-text-muted)">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <p className="text-sm text-(--color-text-muted)">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-(--color-text-primary)">
                {deleteTarget.username}
              </span>
              ?
            </p>
            <div className="flex gap-3 justify-end pt-1">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-md text-sm font-medium border border-(--color-border) text-(--color-text-primary) hover:bg-(--color-bg) transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteTarget.id)}
                disabled
                className="px-4 py-2 rounded-md text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
